import type { CSSProperties } from 'react';
import type { ISkillData } from './index';

/**
 * Skill 详情页 Tab 枚举
 */
export enum ESkillDetailTab {
  /** 使用说明 */
  USAGE = 'usage',
  /** 联动案例 */
  LINKAGE = 'linkage',
  /** 版本历史 */
  VERSION = 'version',
}

/**
 * Skill 详情页展示视图模型（Layer 2 计算后供 UI 使用）
 */
export interface ISkillDisplayViewModel {
  /** 展示名称 */
  displayName: string;
  /** 描述文案 */
  description: string;
  /** 来源标签文案 */
  sourceText: string;
  /** 分类标签文案 */
  categoryText: string;
  /** 作者展示名 */
  authorDisplayName: string;
  /** 作者头像地址 */
  authorAvatarSrc: string;
  /** 分类标签行内样式 */
  categoryTagStyle?: CSSProperties;
  /** 更新时间展示文案 */
  updateTimeText: string;
  /** 使用量展示文案 */
  dialogCountText: string;
  /** 是否展示 Knot 跳转入口 */
  showKnot: boolean;
  /** Knot 跳转链接 */
  knotLink: string;
}

/**
 * 联动案例项（GET /api/v1/util/skills/linkages/list）
 */
export interface ISkillLinkage {
  /** 主键 ID */
  id: number;
  /** 关联 Skill 唯一标识 */
  skill_id: string;
  /** 案例标题（中文） */
  title: string;
  /** 案例标题（英文） */
  title_en: string;
  /** 提问示例（中文） */
  question_example: string;
  /** 提问示例（英文） */
  question_example_en: string;
  /**
   * 案例展示类型：
   * - image：静态截图
   * - session_id：对话回放
   * 缺省按 image 处理
   */
  case_display_type?: 'image' | 'session_id';
  /**
   * 会话范围，仅 case_display_type === 'session_id' 时有意义
   * - session：完整上下文（全部消息）
   * - message：单轮问答
   * - 空串：图片案例
   */
  case_session_type?: 'session' | 'message' | '';
  /**
   * 中文案例 ID，纯 ID，无 session& / message& 前缀
   */
  case_display_session_id?: string;
  /**
   * 英文案例 ID，纯 ID，无 session& / message& 前缀
   */
  case_display_session_id_en?: string;
  /** 截图地址（中文） */
  screenshot_url: string;
  /** 截图地址（英文） */
  screenshot_url_en: string;
  /** 排序值，越小越靠前 */
  sort: number;
  /** 创建时间 */
  create_time: string;
  /** 更新时间 */
  update_time: string;
}

/**
 * 联动案例列表响应
 */
export interface IGetSkillLinkagesResponse {
  total: number;
  page: number;
  page_size: number;
  list: ISkillLinkage[];
}

/**
 * Skill 版本记录（GET /api/v1/util/skills/versions/list）
 */
export interface ISkillVersion {
  /** 主键 ID */
  id: number;
  /** 关联 Skill 唯一标识 */
  skill_id: string;
  /** 版本号，如 1.0.0 */
  version: string;
  /** 版本更新说明 */
  update_history: string;
  /** 版本更新说明（英文） */
  update_history_en: string;
  /** 创建时间 */
  create_time: string;
  /** 更新时间 */
  update_time: string;
}

/**
 * 版本列表请求参数（GET /api/v1/util/skills/versions/list）
 */
export interface IGetSkillVersionsListParams {
  /** Skill 唯一标识 */
  skill_id: string;
  /** 页码，从 1 开始 */
  page?: number;
  /** 每页条数，最大 100 */
  page_size?: number;
  /** 排序字段 */
  sort_item?: string;
  /** 排序方向 */
  sort?: 'asc' | 'desc';
}

/**
 * 版本列表响应
 */
export interface IGetSkillVersionsResponse {
  total: number;
  page: number;
  page_size: number;
  list: ISkillVersion[];
}

/**
 * Skill 正文内容（zh_md / en_md 字段）
 */
export interface ISkillMdContent {
  /** Markdown 正文 */
  content: string;
  /** 文件大小（字节），仅 zip 解析场景有值，读字段场景无该值 */
  size?: number;
}

/**
 * Skill 详情页 Header 组件 Props
 */
export interface ISkillDetailHeaderProps {
  /** 展示视图模型 */
  display: ISkillDisplayViewModel;
  /** 原始 Skill 数据（图标等） */
  skill: ISkillData;
  /** 当前激活 Tab */
  activeTab: ESkillDetailTab;
  /** Tab 切换回调 */
  onTabChange: (tab: ESkillDetailTab) => void;
  /** 点击立即对话 */
  onChatNow: () => void;
  /** 安装 Skill */
  onInstall: (skillId: string) => void;
  /** 下载 Skill 包 */
  onDownload: (skill: ISkillData) => void;
  /** 是否英文环境 */
  isEN: boolean;
  /** 是否正在下载 */
  isDownloading?: boolean;
  /** 返回 Skills 集列表页回调 */
  onBack: () => void;
}
